import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFormContext } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  NotificationRule,
  notificationRuleBaseSchema,
} from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { EditNotificationRuleMessageTemplateForm } from './edit-notification-rule-message-template-form.tsx';
import { EditNotificationRuleConditionsForm } from './edit-notification-rule-conditions-form';
import { useSession } from '@/services/providers/session-provider.tsx';
import { NotificationRuleFormData } from '@/features/notification-rules/schemas/create-notification-rule.schema.ts';
import { useGetUsersQuery } from '@/features/users/hooks/queries/get-all-users.tsx';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { useGetPlantUsersQuery } from '@/features/plants/hooks/queries/[plantId]/[user]/use-get-plant-users.ts';
import { MultiSelect } from '@/components/ui/multi-select.tsx';
import { usePermission } from '@/hooks/use-permission.ts';
import { LoaderIcon } from 'lucide-react';
import { usePatchNotificationRuleMutation } from '@/features/notification-rules/hooks/mutations/use-patch-notification-rule.ts';

interface NotificationRuleEditFormProps {
  notificationRule: NotificationRule;
  onClose?: () => void;
}

export function NotificationRuleEditForm({
  notificationRule,
  onClose,
}: NotificationRuleEditFormProps) {
  const form = useForm({
    resolver: zodResolver(
      notificationRuleBaseSchema.extend({
        forwardTo: z.array(z.string()).optional(),
      })
    ),
    defaultValues: {
      ...notificationRule,
      forwardTo: undefined,
    },
    mode: 'onChange',
  });

  const canForwardTo = usePermission({
    resource: 'notification-rules',
    action: 'forwardTo',
  });

  const { mutate, isPending } = usePatchNotificationRuleMutation({
    onSuccess: () => {
      onClose?.();
    },
  });

  const onSubmit = (data: NotificationRule) => {
    mutate({
      notificationRuleId: data.notificationRuleId,
      cooldownPeriod: Number(data.cooldownPeriod),
      status: data.status,
      forwardTo: [],
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="message">Message</TabsTrigger>
            <TabsTrigger value="conditions">Conditions</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-2">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Status</FormLabel>
                    <FormDescription>
                      Enable or disable this notification rule.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value === 'enabled'}
                      onCheckedChange={(checked) =>
                        field.onChange(checked ? 'enabled' : 'disabled')
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cooldownPeriod"
              render={({ field }) => (
                <FormItem className="mb-2">
                  <FormLabel>Cooldown Period (minutes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="e.g., 60"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <FormDescription>
                    Time before the same alert can be triggered again (60-10080 minutes).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {canForwardTo && <ForwardToSelectFormNotificationRules />}
          </TabsContent>

          <TabsContent value="message" className="mt-2">
            <EditNotificationRuleMessageTemplateForm />
          </TabsContent>

          <TabsContent value="conditions" className="mt-2">
            <EditNotificationRuleConditionsForm />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2">
          {Boolean(onClose) && (
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isPending}>
            {isPending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  );
}

function ForwardToSelectFormNotificationRules() {
  const { email } = useSession();
  const form = useFormContext<NotificationRuleFormData>();
  const watchSelectedPlantId = form.watch('plantId');

  const { data: users, isLoading: areUsersLoading } = useGetUsersQuery();
  const { data: plants, isLoading: arePlantsLoading } = useGetPlantsQuery({
    includeOverview: false,
  });

  const selectedPlant = plants?.find((p) => p.plantId === watchSelectedPlantId);

  const { data: availableUserIds, isLoading: arePlantUsersLoading } = useGetPlantUsersQuery({
    plantId: selectedPlant?.plantId || '',
    enabled: Boolean(selectedPlant?.plantId),
  });

  const availableUsers = users
    ?.filter(
      (user) =>
        (availableUserIds?.userIds?.includes(user.userId) ||
          user.role === 'Company administrator' ||
          user.role === 'Company owner') &&
        user.email !== email
    )
    ?.map((user) => ({
      value: user.userId,
      label: user.email,
      icon: undefined,
    }));

  const isLoading = areUsersLoading || arePlantsLoading || arePlantUsersLoading;

  return (
    <FormField
      control={form.control}
      name="forwardTo"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Forward To</FormLabel>
          <MultiSelect
            options={availableUsers ?? []}
            onValueChange={field.onChange}
            defaultValue={field.value}
            placeholder={isLoading ? 'Loading users...' : 'Select users'}
            variant="inverted"
            maxCount={3}
            disabled={isLoading}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
